import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Alert, Box, createTheme, Skeleton, Snackbar } from "@mui/material";
import { ThemeProvider } from "@emotion/react";

import { themeOptions } from "../../../theme";

// Actions
import {
  clearCreateLeague,
  clearDeleteLeague,
  clearEditLeague,
  createLegue,
  deleteLeague,
  editLeague,
  listAllLeague,
  listAllPointSystem,
} from "../../../redux/actions/league-action";

// Components
import ComponentHeader from "../../ComponentHeader";
import SeasonListTable from "../SeasonListTable";
import MainModal from "../../MainModal";
import CreateLeague from "./CreateLeague";
import DeleteLeague from "./DeleteLeague";
import { useNavigate } from "react-router-dom";

const SeasonList = () => {
  const theme = createTheme(themeOptions);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const _leagueData = {
    name: "",
    seasonID: "",
    league_type: "",
    handicap_diffrential: "100%",
    third_position_play_off: "false",
    no_of_players: "",
    scoringMethod: "",
    noOfTeams: "",
    playersPerTeam: "",
    noOfCaptains: "",
    matchesPerRound: "",
    pointsystemID: "",
    leagueIDforUpdate: "",
  };

  // Selectors
  const allLeagues = useSelector((state) => state.listLeagueReducer);
  let leagueListData = allLeagues?.data?.leagues;
  const createLeagueSelector = useSelector(
    (state) => state.createLeagueReducer
  );
  const editLeagueSelector = useSelector((state) => state.editLeagueReducer);
  const deleteLeagueSelector = useSelector(
    (state) => state.deleteLeagueReducer
  );

  // Local States
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setEditOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [leagueData, setLeagueData] = useState(_leagueData);
  const [deleteLeagueId, setDeleteLeagueId] = useState(null);
  const [search, setSearch] = useState("");
  const [showSnack, setShowSnack] = useState({
    display: false,
    message: "",
  });

  // Logic
  if (search) {
    leagueListData = leagueListData?.filter((val) => {
      if (
        val?.name.toLowerCase().includes(search.toLowerCase()) ||
        val?.league_type.toLowerCase().includes(search.toLowerCase())
      )
        return val;
    });
  }

  const handleFieldChange = (value, key) =>
    setLeagueData((prev) => ({ ...prev, [key]: value }));

  const leagueCreateDispatcher = (payload) => {
    dispatch(createLegue(payload));
  };

  const leagueEditDispatcher = (payload, leagueID) => {
    dispatch(editLeague(payload, leagueID));
  };

  const handleCreateLeague = (mode) => {
    // Necessary fields
    const {
      name,
      seasonID,
      league_type,
      third_position_play_off,
      handicap_diffrential,
      city_id,
      leagueIDforUpdate,
    } = leagueData;

    // Final Payload for creating a league
    let playoffOnThird = third_position_play_off.toString();
    let leagueFinalPayload = {
      name,
      city_id,
      type: league_type,
      season: seasonID,
      handicap: handicap_diffrential,
      playoffOnThird,
    };

    if (leagueData.league_type == "POTY") {
      const { pointsystemID } = leagueData;
      leagueFinalPayload = { ...leagueFinalPayload, pointsystemID };
    }

    if (leagueData.league_type == "SMP") {
      const { third_position_play_off, no_of_players } = leagueData;

      leagueFinalPayload = {
        ...leagueFinalPayload,
        playoffOnThird: third_position_play_off,
        noOfPlayers: no_of_players,
      };
    }

    if (leagueData.league_type == "DMP") {
      const { third_position_play_off, scoringMethod, noOfTeams } = leagueData;
      leagueFinalPayload = {
        ...leagueFinalPayload,
        scoringMethod,
        noOfTeams,
        playoffOnThird: third_position_play_off,
      };
    }

    if (leagueData.league_type == "LCL") {
      const { noOfTeams, playersPerTeam, noOfCaptains, matchesPerRound } =
        leagueData;

      leagueFinalPayload = {
        ...leagueFinalPayload,
        noOfTeams,
        playersPerTeam,
        noOfCaptains,
        matchesPerRound,
      };
    }

    if (mode == "create") leagueCreateDispatcher(leagueFinalPayload);
    if (mode == "update")
      leagueEditDispatcher(leagueFinalPayload, leagueIDforUpdate);
  };

  const handleOpenEditModal = () => setEditOpenModal(true);

  const onDelete = (leagueId) => {
    setOpenDeleteModal(true);
    setDeleteLeagueId(leagueId);
  };

  const handleDeleteLeague = () => {
    dispatch(deleteLeague(deleteLeagueId));
  };

  // useEffect

  useEffect(() => {
    dispatch(listAllLeague());
    dispatch(listAllPointSystem());
    setShowSnack({ display: false, message: "" });

    return () => {
      dispatch(clearDeleteLeague());
      dispatch(clearCreateLeague());
      dispatch(clearEditLeague());
    };
  }, []);

  useEffect(() => {
    if (!createLeagueSelector.loading && !createLeagueSelector?.data?.error) {
      setOpenCreateModal(false);
      if (createLeagueSelector?.data?.error != undefined) {
        setShowSnack({ display: true, message: "League Created Successfully" });
      }
      dispatch(listAllLeague());
      setLeagueData(_leagueData);
    }
  }, [createLeagueSelector]);

  useEffect(() => {
    if (!editLeagueSelector.loading && !editLeagueSelector?.data?.error) {
      setEditOpenModal(false);
      if (editLeagueSelector?.data?.error != undefined) {
        setShowSnack({
          display: true,
          message: editLeagueSelector?.data?.message,
        });
      }
      dispatch(listAllLeague());
      setLeagueData(_leagueData);
    }
  }, [editLeagueSelector]);

  useEffect(() => {
    if (!deleteLeagueSelector?.loading && !deleteLeagueSelector?.data?.error) {
      setOpenDeleteModal(false);
      if (deleteLeagueSelector?.data?.error != undefined) {
        setShowSnack({
          display: true,
          message: deleteLeagueSelector?.data?.message,
        });
      }
      setDeleteLeagueId(null);
      dispatch(listAllLeague());
    }
  }, [deleteLeagueSelector]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 2 }}>
        <ComponentHeader
          headerTitle="League List"
          cbOpenModal={() => {
            setOpenCreateModal(true);
            setLeagueData(_leagueData);
          }}
          onSearch={(e) => setSearch(e)}
        />
      </Box>

      <Box sx={{ p: 2 }}>
        {!allLeagues?.loading && !allLeagues?.error ? (
          <SeasonListTable
            leagueListData={leagueListData}
            openEditModal={handleOpenEditModal}
            setLeagueData={setLeagueData}
            onDelete={onDelete}
          />
        ) : (
          <>
            <Skeleton height={80} />
            <Skeleton animation="wave" height={80} />
            <Skeleton animation={false} height={80} />
          </>
        )}
      </Box>

      <MainModal
        show={openCreateModal}
        closeModal={() => setOpenCreateModal(false)}
        modalHeadTitle="Create League"
        overflowY="scroll"
        btnLeftTitle="Create New League"
        cb={() => handleCreateLeague("create")}
        width="65%"
        leftVariant="contained"
      >
        <CreateLeague
          leagueData={leagueData}
          handleFieldChange={handleFieldChange}
        />
      </MainModal>

      <MainModal
        show={openEditModal}
        closeModal={() => setEditOpenModal(false)}
        modalHeadTitle="Edit League"
        overflowY="scroll"
        btnLeftTitle="Update"
        leftVariant="contained"
        cb={() => handleCreateLeague("update")}
        width="65%"
      >
        <CreateLeague
          leagueData={leagueData}
          handleFieldChange={handleFieldChange}
        />
      </MainModal>

      <MainModal
        show={openDeleteModal}
        closeModal={() => setOpenDeleteModal(false)}
        btnCloseModal={() => setOpenDeleteModal(false)}
        modalHeadTitle="Delete League"
        overflowY="scroll"
        btnLeftTitle={deleteLeagueSelector?.loading ? "Loading" : "Delete"}
        btnRightTitle="Cancel"
        variant="contained"
        btnLeftsx={{
          borderRadius: "5px !important",
          backgroundColor: "#06623B",
          color: "#fff",
          fontWeight: 700,
          minWidth: "100px",
          ":hover": { backgroundColor: "#06623B" },
        }}
        btnRightsx={{
          borderRadius: "5px !important",
          backgroundColor: "#F2F2F2",
          color: "#2F4F4F",
          fontWeight: 700,
          minWidth: "100px",
        }} // cb={() => handleCreateLeague("update")}
        width="30%"
        cb={handleDeleteLeague}
      >
        <DeleteLeague />
      </MainModal>

      <Snackbar
        open={showSnack.display}
        onClose={() => setShowSnack((prev) => ({ ...prev, display: false }))}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert>{showSnack.message}</Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default SeasonList;
